import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { maxPageSize } from '@app/global-constants';
import { IConfigPersonSearch } from '@components/person-search-modal/person-search-modal.interface';
import { IPagination } from '@interfaces/common.interface';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize, Observable } from 'rxjs';

/*

  USE EXAMPLE

  this.modalService.show(PersonSearchModalComponent, {
    class: 'modal-dialog-centered modal-dialog-scrollable modal-lg',
    initialState: {
      config: {
        multipleSelection: true/false,
        method: this.personService.getRecords,
        parent: this.personService,
        fields: [
          { placeholder: $localize`Nazwisko`, propsName: 'studentNazwisko' }, <- wersja z kluczem translacji
          { placeholder: $localize`Imię`, propsName: 'studentImie' },
          { placeholder: $localize`Numer albumu`, propsName: 'studentNumerAlbumu', noFilter: true }, <- noFilter wskazuje, ze po danym polu nie ma filtrowania
        ],
        comboboxFields: [
          {
            placeholder: $localize`Jednostka`,
            parent: this.unitService,
            method: this.unitService.getRecords,
            propSearchName: 'jednostkaId', <- props do filtrowania
            propsName: 'jednostkaNazwa', <- props do wyświetlenia w tabeli
            propToReturn: 'id',
            propToDisplay: 'nazwa'
          }
        ],
        itemId: 'id',
        requestDefaultParams: {
            programStudiowId: this.practiceData.programStudiowId
        }
      }
    }
  });

*/

@Component({
  selector: 'app-person-search-modal',
  templateUrl: './person-search-modal.component.html',
})
export class PersonSearchModalComponent implements OnInit {
  @Input() config: IConfigPersonSearch<unknown>;
  @Output() readonly submitModal = new EventEmitter<unknown[]>();
  isSaving: boolean;
  persons: Record<string, unknown>[];
  selectedPersons: unknown[] = [];
  formFilters: UntypedFormGroup;
  isLoadingData: boolean;
  adaptToStudyProgram: boolean;
  comboboxItems: unknown[][] = [];
  comboboxConfigs: {
    initialTextValue?: string;
    placeholder?: string;
    keyToReturn?: string;
    multipleKeysToReturn?: string[];
    keyToDisplay: string;
    displayPattern?: {
      keys: string[];
      separator: string;
    };
    returnAllObjectData?: boolean;
    validator?: ValidatorFn | ValidatorFn[];
  }[] = [];
  page = 0;
  totalItems: number;
  pageSize = 10;

  constructor(readonly modalRef: BsModalRef) {}

  ngOnInit(): void {
    if (!Array.isArray(this.config.arguments)) {
      this.config.arguments = [];
    }
    this.createFilterForm();
    this.createComboboxItemsAndConfigs();
    this.getRecords({ page: this.page });
  }

  getFieldsNumber(): number {
    return (this.config.comboboxFields?.length ?? 0) + (this.config.fields?.length ?? 0);
  }

  changeListSize(value: number): void {
    this.page = 0;
    this.pageSize = value;
    this.getRecords({ page: this.page });
  }

  getRecords(ev: IPagination): void {
    this.isLoadingData = true;
    const params = {
      page: ev.page,
      pageSize: this.pageSize,
      ...this.getFilledFiltersProps(),
      ...this.config.requestDefaultParams,
    };
    const fnArgs = [...this.config.arguments, params];
    const requestFn: () => Observable<any> = this.config.method.bind(this.config.parent, ...fnArgs);

    requestFn()
      .pipe(
        finalize(() => {
          this.isLoadingData = false;
        }),
      )
      .subscribe((res: { data: Record<string, unknown>[]; total: number }) => {
        if (res) {
          this.persons = res.data.map((item) => ({
            ...item,
            isSelected: this.selectedPersons.find(
              (el) => el[this.config.itemId as keyof typeof el] === item[this.config.itemId as keyof typeof el],
            ),
          }));
          this.totalItems = res.total;
        } else {
          this.totalItems = 0;
        }
      });
  }

  setFilterValueFromCombobox(value: string, formControlName: string): void {
    this.formFilters.get(formControlName).setValue(value || '');
  }

  getComboboxItems(index: number): void {
    const comboboxParams = { pageSize: maxPageSize };

    const requestFn = this.config.comboboxFields[index].method.bind(
      this.config.comboboxFields[index].parent,
      comboboxParams,
    );
    requestFn().subscribe((res: { data: unknown[] }) => {
      this.comboboxItems[index] = res.data;
    });
  }

  trackBy({ a = {} }: Record<string, unknown>, item: Record<string, string>): string {
    return item.id;
  }

  toggleSelectedPerson(item: Record<string, any>): void {
    if (!this.config.multipleSelection) {
      this.persons.forEach((el) => (el.isSelected = false));
    }
    const indexPerson = this.selectedPersons.findIndex(
      (el) => el[this.config.itemId as keyof typeof el] === item[this.config.itemId as keyof typeof el],
    );
    if (indexPerson !== -1) {
      item.isSelected = false;
      this.selectedPersons.splice(indexPerson, 1);
      return;
    }
    item.isSelected = true;
    this.config.multipleSelection ? this.selectedPersons.push(item) : (this.selectedPersons = [item]);
  }

  resetFilters(): void {
    this.page = 0;
    this.formFilters.reset();
    this.getRecords({ page: this.page });
  }

  submitFilters(): void {
    this.page = 0;
    this.getRecords({ page: this.page });
  }

  submit(): void {
    this.submitModal.emit(this.selectedPersons);
  }

  private createFilterForm(): void {
    this.formFilters = new UntypedFormGroup({});
    for (const item of this.config.fields) {
      this.formFilters.addControl(item.propsName, new UntypedFormControl(null));
    }
    if (this.config.comboboxFields?.length) {
      for (const item of this.config.comboboxFields) {
        this.formFilters.addControl(item.propSearchName, new UntypedFormControl(null));
      }
    }
  }

  private getFilledFiltersProps(): Record<string, unknown> {
    let props = {};
    for (const item in this.formFilters.value) {
      if ({}.hasOwnProperty.call(this.formFilters.value, item)) {
        if (this.formFilters.value[item]) {
          props = {
            ...props,
            [item]: this.formFilters.value[item],
          };
        }
      }
    }
    return props;
  }

  private createComboboxItemsAndConfigs(): void {
    if (this.config.comboboxFields?.length) {
      this.config.comboboxFields.forEach((config, i) => {
        const { placeholder, propToDisplay, propToReturn } = config;
        this.comboboxItems = [...this.comboboxItems, []];
        this.comboboxConfigs = [
          ...this.comboboxConfigs,
          { placeholder, keyToDisplay: propToDisplay, keyToReturn: propToReturn },
        ];
        this.getComboboxItems(i);
      });
    }
  }
}
